import { useState } from "react";
import { useQuery, useAction, useMutation, useConvexAuth } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import { Link, Navigate } from "react-router";
import { errorMessage, GENERIC_ERROR_MESSAGE } from "@todotogether/shared";
import { api } from "@todotogether/backend/_generated/api";

/**
 * Settings (ticket #18): the one persistent paywall surface. Shows the
 * plan as Convex serves it (entitlements:getMine — the synced entitlement,
 * never RevenueCat) and, for free Users, the checkout CTA. Everywhere else
 * the paywall appears only at the moment a limit bites. Paid Users get the
 * self-serve way out instead (ticket #50): a hosted Stripe Customer Portal
 * session for cancel / payment-method updates.
 *
 * The Account section is the self-service bucket: display name, change
 * email, change password (the latter two password-confirmed and
 * enforced server-side in convex/accounts.ts) and account deletion
 * (#206). The Display section holds per-user view preferences — the
 * "Show due dates on items" toggle (#207), stored server-side so it
 * follows the user across devices and sessions.
 */
export default function Settings() {
  // Gate on the auth lifecycle first (same pattern as App): a query that
  // fires before the session settles comes back null, which must read as
  // "still loading", not "signed out".
  const { isLoading } = useConvexAuth();
  const entitlement = useQuery(api.entitlements.getMine);
  const myLists = useQuery(api.lists.myLists);
  const createCheckoutUrl = useAction(api.billing.createCheckoutUrl);
  const createPortalUrl = useAction(api.billing.createPortalUrl);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [portalError, setPortalError] = useState<string | null>(null);
  // A checkout/portal CTA is in flight — buttons disable together.
  const [busy, setBusy] = useState(false);

  if (isLoading || entitlement === undefined || myLists === undefined) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-paper">
        <p className="text-muted" data-testid="settings-loading">
          Loading…
        </p>
      </main>
    );
  }
  if (entitlement === null || myLists === null) {
    return <Navigate to="/signin" replace />;
  }

  const paid = entitlement.tier === "paid";

  async function upgrade(plan: "monthly" | "yearly") {
    setUpgradeError(null);
    setBusy(true);
    try {
      const { url } = await createCheckoutUrl({ plan });
      window.location.href = url;
    } catch (err) {
      setUpgradeError(errorMessage(err, GENERIC_ERROR_MESSAGE));
    } finally {
      setBusy(false);
    }
  }

  async function manageSubscription() {
    setPortalError(null);
    setBusy(true);
    try {
      const { url } = await createPortalUrl({});
      window.location.href = url;
    } catch (err) {
      setPortalError(errorMessage(err, GENERIC_ERROR_MESSAGE));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-paper px-6 py-10">
      <div className="mx-auto max-w-2xl">
        <Link to="/app" className="text-sm text-muted hover:underline" data-testid="back-to-app">
          ← Your lists
        </Link>
        <h1 className="mt-4 text-2xl font-bold text-ink">Settings</h1>

        <section
          className="mt-6 rounded-xl border border-line bg-page p-4 shadow-sm"
          data-testid="billing-section"
        >
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
            Plan
          </h2>
          <p className="mt-2 text-base font-medium text-ink" data-testid="plan-tier">
            {paid ? "Paid plan" : "Free plan"}
          </p>
          <p className="mt-1 text-sm text-muted" data-testid="quota-summary">
            Up to {entitlement.listLimit} lists you own and{" "}
            {entitlement.memberLimit} members per list
            {myLists.owned.length > 0 && ` — you own ${myLists.owned.length}`}.
          </p>
          {!paid && (
            <div className="mt-3">
              <button
                type="button"
                data-testid="upgrade-cta"
                onClick={() => void upgrade("monthly")}
                disabled={busy}
                className="rounded-lg bg-blue-fill px-4 py-2 text-sm font-medium text-on-blue shadow-sm hover:bg-blue-fill-pressed disabled:opacity-60"
              >
                Upgrade — £1.50/month
              </button>{" "}
              <button
                type="button"
                data-testid="upgrade-cta-yearly"
                onClick={() => void upgrade("yearly")}
                disabled={busy}
                className="rounded-lg border border-line bg-page px-4 py-2 text-sm font-medium text-ink shadow-sm hover:bg-pressed disabled:opacity-60"
              >
                Yearly — £12/year
              </button>
              {upgradeError && (
                <p className="mt-2 text-sm text-danger" data-testid="upgrade-error">
                  {upgradeError}
                </p>
              )}
            </div>
          )}
          {paid && (
            <div className="mt-3">
              {/* Self-serve lifecycle (ticket #50): Stripe's hosted portal —
                  no cancel/payment UI of our own, and no upgrade CTA to nag a
                  paying User with. */}
              <button
                type="button"
                data-testid="manage-subscription-cta"
                onClick={() => void manageSubscription()}
                disabled={busy}
                className="rounded-lg border border-line bg-page px-4 py-2 text-sm font-medium text-ink shadow-sm hover:bg-pressed disabled:opacity-60"
              >
                Manage subscription
              </button>
              {portalError && (
                <p className="mt-2 text-sm text-danger" data-testid="portal-error">
                  {portalError}
                </p>
              )}
            </div>
          )}
        </section>

        <DisplaySection />

        <AccountSection />
      </div>
    </main>
  );
}

/**
 * Display preferences (ticket #207): one toggle, "Show due dates on
 * items", wired to the server-side per-user preference
 * (users:setShowDueDates; users.showDueDates, absent = on). It gates
 * the due badge on item rows only — the lists screen's "Next due"
 * panel is deliberately unaffected, and setting/clearing due dates
 * keeps working while the text is hidden. Each member of a shared list
 * controls only their own view; other members see theirs unchanged.
 */
function DisplaySection() {
  const me = useQuery(api.users.me);
  const setShowDueDates = useMutation(api.users.setShowDueDates);
  const [error, setError] = useState<string | null>(null);
  // While `me` loads the toggle renders the default (on) but stays
  // disabled — a write before the session settles would race the
  // lifecycle gate the page itself waits on. Same absent=on spelling
  // as the row gate in ListDetail.
  const show = me?.showDueDates ?? true;
  return (
    <section
      className="mt-4 rounded-xl border border-line bg-page p-4 shadow-sm"
      data-testid="display-section"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
        Display
      </h2>
      <label className="mt-2 flex items-center gap-2 text-sm text-muted">
        <input
          type="checkbox"
          data-testid="show-due-dates-toggle"
          checked={show}
          disabled={me === undefined}
          onChange={(e) => {
            setError(null);
            void setShowDueDates({ show: e.target.checked }).catch((err) =>
              setError(errorMessage(err, GENERIC_ERROR_MESSAGE)),
            );
          }}
          className="h-4 w-4"
        />
        Show due dates on items
      </label>
      {error && (
        <p className="mt-1 text-sm text-danger" data-testid="display-error">
          {error}
        </p>
      )}
    </section>
  );
}

const inputClass =
  "w-full rounded-lg border border-line bg-page px-3 py-2 text-sm text-ink placeholder:text-faint shadow-sm focus:border-blue focus:outline-none";

/**
 * Account self-service: display name, change email, change password,
 * and account deletion. The password-gated forms only render for
 * password accounts — OAuth-only users see why instead of a form that
 * can only error. DeleteAccountSection deliberately sits OUTSIDE that
 * gate: deletion renders for every account kind (#16's GDPR / App Store
 * requirement applies to any account, and this is the product's only
 * delete surface).
 */
function AccountSection() {
  const me = useQuery(api.users.me);
  const methods = useQuery(api.accounts.myAuthMethods);
  const setName = useMutation(api.accounts.setName);
  const changeEmail = useAction(api.accounts.changeEmail);
  const changePassword = useAction(api.accounts.changePassword);

  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [nameSaved, setNameSaved] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const [emailDraft, setEmailDraft] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSaved, setEmailSaved] = useState(false);

  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSaved, setPwSaved] = useState(false);

  const hasPassword = methods?.password === true;

  async function saveName() {
    setNameSaved(false);
    setNameError(null);
    try {
      await setName({ name: nameDraft ?? me?.name ?? "" });
      setNameSaved(true);
    } catch (err) {
      setNameError(errorMessage(err, GENERIC_ERROR_MESSAGE));
    }
  }

  async function submitEmail() {
    setEmailError(null);
    setEmailSaved(false);
    try {
      await changeEmail({ password: emailPassword, newEmail: emailDraft });
      setEmailSaved(true);
      setEmailDraft("");
      setEmailPassword("");
    } catch (err) {
      setEmailError(errorMessage(err, GENERIC_ERROR_MESSAGE));
    }
  }

  async function submitPassword() {
    setPwError(null);
    setPwSaved(false);
    if (pwNew !== pwConfirm) {
      setPwError("New passwords don't match");
      return;
    }
    try {
      await changePassword({
        currentPassword: pwCurrent,
        newPassword: pwNew,
      });
      setPwSaved(true);
      setPwCurrent("");
      setPwNew("");
      setPwConfirm("");
    } catch (err) {
      setPwError(errorMessage(err, GENERIC_ERROR_MESSAGE));
    }
  }

  return (
    <section
      className="mt-4 rounded-xl border border-line bg-page p-4 shadow-sm"
      data-testid="account-section"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
        Account
      </h2>
      <p className="mt-2 text-sm text-muted">
        Signed in as{" "}
        <span className="font-medium text-ink" data-testid="account-email">
          {me?.email ?? "…"}
        </span>
      </p>

      {/* Display name */}
      <form
        className="mt-4 flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void saveName();
        }}
      >
        <input
          data-testid="account-name-input"
          value={nameDraft ?? me?.name ?? ""}
          onChange={(e) => {
            setNameDraft(e.target.value);
            setNameSaved(false);
          }}
          placeholder="Display name (optional)"
          className={`${inputClass} max-w-xs flex-1`}
        />
        <button
          type="submit"
          data-testid="account-name-save"
          className="rounded-lg border border-line bg-page px-3 py-1.5 text-sm font-medium text-muted shadow-sm hover:bg-pressed"
        >
          Save name
        </button>
        {nameError && (
          <span className="text-sm text-danger" data-testid="account-name-error">
            {nameError}
          </span>
        )}
        {nameSaved && (
          <span className="text-xs text-muted" data-testid="account-name-saved">
            Saved — shared lists will show your name instead of your email.
          </span>
        )}
      </form>

      {methods === undefined ? null : hasPassword ? (
        <>
          {/* Change email */}
          <form
            className="mt-5 space-y-2 border-t border-line pt-4"
            onSubmit={(e) => {
              e.preventDefault();
              void submitEmail();
            }}
          >
            <h3 className="text-sm font-medium text-muted">Change email</h3>
            <input
              type="email"
              data-testid="change-email-input"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              placeholder="New email address"
              className={inputClass}
            />
            <input
              type="password"
              data-testid="change-email-password"
              value={emailPassword}
              onChange={(e) => setEmailPassword(e.target.value)}
              placeholder="Current password"
              className={inputClass}
            />
            {emailError && (
              <p className="text-sm text-danger" data-testid="change-email-error">
                {emailError}
              </p>
            )}
            {emailSaved && (
              <p className="text-sm text-blue" data-testid="change-email-saved">
                Email updated — use it to sign in from now on.
              </p>
            )}
            <button
              type="submit"
              data-testid="change-email-submit"
              className="rounded-lg border border-line bg-page px-3 py-1.5 text-sm font-medium text-muted shadow-sm hover:bg-pressed"
            >
              Update email
            </button>
          </form>

          {/* Change password */}
          <form
            className="mt-5 space-y-2 border-t border-line pt-4"
            onSubmit={(e) => {
              e.preventDefault();
              void submitPassword();
            }}
          >
            <h3 className="text-sm font-medium text-muted">Change password</h3>
            <input
              type="password"
              data-testid="change-password-current"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              placeholder="Current password"
              className={inputClass}
            />
            <input
              type="password"
              data-testid="change-password-new"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              placeholder="New password (min 8 characters)"
              className={inputClass}
            />
            <input
              type="password"
              data-testid="change-password-confirm"
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
              placeholder="Confirm new password"
              className={inputClass}
            />
            {pwError && (
              <p className="text-sm text-danger" data-testid="change-password-error">
                {pwError}
              </p>
            )}
            {pwSaved && (
              <p className="text-sm text-blue" data-testid="change-password-saved">
                Password updated — other devices have been signed out.
              </p>
            )}
            <button
              type="submit"
              data-testid="change-password-submit"
              className="rounded-lg border border-line bg-page px-3 py-1.5 text-sm font-medium text-muted shadow-sm hover:bg-pressed"
            >
              Update password
            </button>
            <p className="text-xs text-muted">
              Changing your password signs out every other device.
            </p>
          </form>
        </>
      ) : (
        <p className="mt-4 text-sm text-muted" data-testid="account-no-password">
          You sign in with Google or Apple, so your email and password are
          managed by that provider.
        </p>
      )}

      <DeleteAccountSection />
    </section>
  );
}

/**
 * Account deletion (ticket #16): two-step confirm, then the server-side
 * cascade wipes owned lists, memberships, assignments and every auth
 * session — the auth gate lands on /signin immediately. #206 moved it
 * here off the lists page's danger-zone banner: Settings is where the
 * other account actions live, and the lists page shouldn't open with
 * warning chrome.
 */
function DeleteAccountSection() {
  const deleteAccount = useMutation(api.accounts.deleteAccount);
  const { signOut } = useAuthActions();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="mt-5 border-t border-line pt-4">
      <h3 className="text-sm font-medium text-muted">Delete account</h3>
      <p className="mt-1 text-xs text-muted">
        Deleting your account permanently removes your owned lists right
        away — no restore window — and removes you from every list you
        joined.
      </p>
      {error && (
        <p className="mt-2 text-sm text-danger" data-testid="delete-account-error">
          {error}
        </p>
      )}
      {confirming ? (
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            data-testid="delete-account-confirm"
            onClick={async () => {
              setError(null);
              try {
                await deleteAccount({});
                await signOut();
              } catch (err) {
                setError(errorMessage(err, GENERIC_ERROR_MESSAGE));
              }
            }}
            className="rounded-lg bg-danger px-3 py-1.5 text-sm font-medium text-on-blue shadow-sm hover:bg-danger-pressed"
          >
            Yes, delete my account forever
          </button>
          <button
            type="button"
            data-testid="delete-account-cancel"
            onClick={() => setConfirming(false)}
            className="rounded-lg border border-line bg-page px-3 py-1.5 text-sm font-medium text-muted shadow-sm hover:bg-pressed"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid="delete-account-start"
          onClick={() => setConfirming(true)}
          className="mt-2 rounded-lg border border-danger/40 bg-page px-3 py-1.5 text-sm font-medium text-danger shadow-sm hover:border-danger hover:bg-danger hover:text-on-blue"
        >
          Delete my account
        </button>
      )}
    </div>
  );
}
